import { create } from 'zustand';
import type { Child, Pathway, Progress, EarnedBadge, EarnedLessonBadge } from '../types';

interface ChildState {
  children: Child[];
  activeChild: Child | null;
  pathways: Pathway[];
  progress: Record<string, Progress>; // keyed by pathwayId
  earnedBadges: EarnedBadge[];
  earnedLessonBadges: EarnedLessonBadge[];
  setChildren: (children: Child[]) => void;
  /** Adds the child, or replaces the entry with the same id — never clobbers siblings. */
  upsertChild: (child: Child) => void;
  setActiveChild: (child: Child | null) => void;
  setPathways: (pathways: Pathway[]) => void;
  setProgress: (pathwayId: string, progress: Progress) => void;
  setBulkProgress: (progress: Record<string, Progress>) => void;
  awardBadge: (id: string) => void;
  awardLessonBadge: (lessonId: string) => void;
  setBadges: (badges: EarnedBadge[], lessonBadges: EarnedLessonBadge[]) => void;
}

export const useChildStore = create<ChildState>((set) => ({
  children: [],
  activeChild: null,
  pathways: [],
  progress: {},
  earnedBadges: [],
  earnedLessonBadges: [],
  setChildren: (children) => set({ children }),
  upsertChild: (child) =>
    set((state) => ({
      children: state.children.some((c) => c.id === child.id)
        ? state.children.map((c) => (c.id === child.id ? child : c))
        : [...state.children, child],
    })),
  setActiveChild: (activeChild) => set({ activeChild }),
  setPathways: (pathways) => set({ pathways }),
  setProgress: (pathwayId, progress) =>
    set((state) => ({ progress: { ...state.progress, [pathwayId]: progress } })),
  setBulkProgress: (progress) => set({ progress }),
  awardBadge: (id) =>
    set((state) => {
      if (state.earnedBadges.some((b) => b.id === id)) return state;
      return { earnedBadges: [...state.earnedBadges, { id, earnedAt: new Date() }] };
    }),
  awardLessonBadge: (lessonId) =>
    set((state) => {
      if (state.earnedLessonBadges.some((b) => b.lessonId === lessonId)) return state;
      return {
        earnedLessonBadges: [
          ...state.earnedLessonBadges,
          { lessonId, earnedAt: new Date() },
        ],
      };
    }),
  setBadges: (badges, lessonBadges) =>
    set({ earnedBadges: badges, earnedLessonBadges: lessonBadges }),
}));
